<div #playerWrapper class="player-wrapper" [class.aspect-locked]="recordingFormat() !== null"
  (pointerdown)="onSeekPointerDown($event)"
  (pointermove)="onSeekPointerMove($event)"
  (pointerup)="onSeekPointerUp($event)"
  (pointercancel)="onSeekPointerCancel($event)">

  <!-- Diagnostics: shown only once a load has failed, and deliberately outside
       .controls-overlay so it stays reachable when the panorama fails and the
       normal controls never appear. -->
  @if (hasError()) {
  <div class="diag-wrapper">
    <button
      class="icon-btn diag-btn"
      type="button"
      [class.active-btn]="showDiagnostics()"
      (pointerdown)="onDiagPointerDown($event)"
      (pointerup)="onDiagPointerUp($event)"
      (pointercancel)="onDiagPointerCancel($event)"
      (click)="onDiagTrigger($event)"
      title="Diagnostics"
      aria-label="Diagnostics">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path fill-rule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm8.706-1.442c1.146-.573 2.437.463 2.126 1.706l-.709 2.836.042-.02a.75.75 0 01.67 1.34l-.04.022c-1.147.573-2.438-.463-2.127-1.706l.71-2.836-.042.02a.75.75 0 11-.671-1.34l.041-.022zM12 9a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd" />
      </svg>
    </button>
    @if (showDiagnostics()) {
      <div class="diag-panel" (click)="$event.stopPropagation()">
        <div class="diag-actions">
          <button class="diag-copy-btn" (click)="copyDiagnostics()">Copy</button>
          <button class="record-item diag-close-btn" (click)="closeDiagnostics()">Close</button>
        </div>
        @if (diagCopied()) {
          <div class="diag-caption">Copied — send this to support</div>
        } @else if (diagCopyFailed()) {
          <div class="diag-caption">Copy failed — select the text below manually</div>
        }
        <pre class="diag-body">{{ diagText() }}</pre>
      </div>
    }
  </div>
  }

  <div #viewerContainer class="psv-container"
       [class.recording-16-9]="recordingFormat() === '16:9'"
       [class.recording-9-16]="recordingFormat() === '9:16'"
       [class.recording-4-5]="recordingFormat() === '4:5'"
       [class.recording-1-1]="recordingFormat() === '1:1'"></div>

  <canvas
    #drawingCanvas
    class="drawing-layer"
    [class.active]="isDrawMode()"
    [class.recording-16-9]="recordingFormat() === '16:9'"
    [class.recording-9-16]="recordingFormat() === '9:16'"
    [class.recording-4-5]="recordingFormat() === '4:5'"
    [class.recording-1-1]="recordingFormat() === '1:1'"
    (mousedown)="startDrawing($event)"
    (mousemove)="draw($event)"
    (mouseup)="stopDrawing()"
    (mouseleave)="stopDrawing()"
    (touchstart)="startDrawing($event)"
    (touchmove)="draw($event)"
    (touchend)="stopDrawing()"
  ></canvas>

  @if (seekFeedbackDirection()) {
    <div class="seek-feedback" [class.backward]="seekFeedbackDirection() === 'backward'" aria-live="polite">
      <div class="seek-feedback-arrows" aria-hidden="true">
        @if (seekFeedbackDirection() === 'backward') { &#x276E;&#x276E; } @else { &#x276F;&#x276F; }
      </div>
      <span>{{ seekFeedbackDirection() === 'backward' ? '-' : '+' }}{{ seekFeedbackSeconds() }} seconds</span>
    </div>
  }

  <div class="controls-overlay" [class.visible]="isUserActive() || !isPlaying() || isScrubbing() || isDrawMode() || hasDrawings() || isRecording()">
    
    <!-- Play/Pause -->
    <button class="icon-btn play-btn" (click)="togglePlay()">
      @if (isPlaying()) {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M6.75 5.25a.75.75 0 01.75-.75H9a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H7.5a.75.75 0 01-.75-.75V5.25zm7.5 0A.75.75 0 0115 4.5h1.5a.75.75 0 01.75.75v13.5a.75.75 0 01-.75.75H15a.75.75 0 01-.75-.75V5.25z" clip-rule="evenodd" />
        </svg>
      } @else {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z" clip-rule="evenodd" />
        </svg>
      }
    </button>

    <!-- Scrubber Group -->
    <div class="scrubber-group">
      <span class="time-display">{{ currentRealTime() | date:'HH:mm:ss' }}</span>

      <div class="slider-container"
        (pointerdown)="onSliderAreaDown($event)"
        (pointermove)="onSliderAreaMove($event)"
        (pointerup)="onSliderAreaUp($event)"
        (pointercancel)="onSliderAreaUp($event)"
        (pointerleave)="onSliderAreaLeave($event)"
      >
        <div #previewContainer class="preview-thumb" [class.visible]="showPreview() && previewEnabled()" [style.left.px]="previewLeftPx()"></div>
        @if (showPreview()) {
          <div class="time-tooltip" [class.lifted]="previewEnabled()" [style.left.px]="previewLeftPx()">
            {{ previewTime() | date:'HH:mm:ss' }}
          </div>
        }
        <input
          #seekBar
          type="range"
          class="seek-bar"
          [min]="0"
          [max]="100"
          [step]="0.1"
          [value]="progressPercentage()"
          (input)="seek($event)"
          (mousedown)="onScrubStart()"
          (mouseup)="onScrubEnd()"
          (touchstart)="onScrubberTouchStart($event)"
          (touchmove)="onScrubberTouch($event)"
          (touchend)="onScrubberTouchEnd()"
        />
      </div>

      <span class="duration-display">{{ endTime() | date:'HH:mm:ss' }}</span>
    </div>

    <!-- Volume Group -->
    <div class="volume-group">
      <button class="icon-btn vol-btn" (click)="toggleMute()">
        @if (isMuted() || volume() === 0) {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM17.78 9.22a.75.75 0 10-1.06 1.06L18.44 12l-1.72 1.72a.75.75 0 101.06 1.06l1.72-1.72 1.72 1.72a.75.75 0 101.06-1.06L20.56 12l1.72-1.72a.75.75 0 10-1.06-1.06l-1.72 1.72-1.72-1.72z" /></svg>
        } @else if (volume() < 0.5) {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.5 12a4.5 4.5 0 00-1.55-3.39l-1.06 1.06a3 3 0 010 4.66l1.06 1.06A4.5 4.5 0 0018.5 12z" /></svg>
        } @else {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 4.06c0-1.336-1.616-2.005-2.56-1.06l-4.5 4.5H4.508c-1.141 0-2.318.664-2.66 1.905A9.76 9.76 0 001.5 12c0 .898.121 1.768.35 2.595.341 1.24 1.518 1.905 2.659 1.905h1.93l4.5 4.5c.945.945 2.561.276 2.561-1.06V4.06zM18.5 12a4.5 4.5 0 00-1.55-3.39l-1.06 1.06a3 3 0 010 4.66l1.06 1.06A4.5 4.5 0 0018.5 12zM22 12c0 2.48-1.01 4.73-2.64 6.36l-1.06-1.06a7.49 7.49 0 010-10.6l1.06-1.06C20.99 7.27 22 9.52 22 12z" /></svg>
        }
      </button>

      <input type="range" class="vol-slider" [min]="0" [max]="1" [step]="0.05" [value]="volume()" (input)="changeVolume($event)"/>
    </div>

    <!-- Drawing Controls -->
    
    <!-- Clear Drawing (Shows if there are ANY drawings, even if draw mode is off) -->
    @if (hasDrawings()) {
      <button class="icon-btn" (click)="clearDrawings()" title="Clear Drawings">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path fill-rule="evenodd" d="M16.5 4.478v.227a48.816 48.816 0 013.878.512.75.75 0 11-.49 1.478 47.369 47.369 0 00-15.776 0 .75.75 0 01-.49-1.478 48.809 48.809 0 013.878-.512V4.478C7.5 2.946 8.796 1.75 10.421 1.75h3.158c1.625 0 2.921 1.196 2.921 2.728zM5.625 7.965a45.626 45.626 0 00-.73 11.23c.033 1.144.962 2.054 2.1 2.054h9.965c1.138 0 2.067-.91 2.1-2.054a45.626 45.626 0 00-.73-11.23 48.91 48.91 0 00-12.704 0z" clip-rule="evenodd" />
        </svg>
      </button>
    }

    <!-- Draw Mode Toggle -->
    <button class="icon-btn" [class.active-btn]="isDrawMode()" (click)="toggleDrawMode()" title="Toggle Draw Mode">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
        <path d="M21.731 2.269a2.625 2.625 0 00-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 000-3.712zM19.513 8.199l-3.712-3.712-12.15 12.15a5.25 5.25 0 00-1.32 2.214l-.8 2.685a.75.75 0 00.933.933l2.685-.8a5.25 5.25 0 002.214-1.32L19.513 8.2z" />
      </svg>
    </button>

    <!-- Settings (cog) -->
    @if (!isCoarsePointer) {
      <div class="settings-wrapper">
        <button class="icon-btn" [class.active-btn]="showSettings()" (click)="toggleSettings($event)" title="Settings">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" transform="translate(2 2)"/>
          </svg>
        </button>
        @if (showSettings()) {
          <div class="settings-menu" (click)="$event.stopPropagation()">
            <button class="settings-item" (click)="togglePreview()">
              <span>Scrub thumbnail preview</span>
              <span class="settings-toggle" [class.on]="previewEnabled()">
                <span class="settings-toggle-knob"></span>
              </span>
            </button>
          </div>
        }
      </div>
    }

    <!-- Record -->
    <div class="record-wrapper">
      <button
        class="icon-btn record-btn"
        [class.recording]="isRecording()"
        (click)="onRecordButtonClick($event)"
        [title]="isRecording() ? 'Stop recording' : 'Record video'"
      >
        @if (isRecording()) {
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2"/>
          </svg>
          <span class="record-timer">{{ formatDuration(recordingElapsedSec()) }}</span>
        } @else {
          <span class="record-dot"></span>
        }
      </button>
      @if (showRecordMenu() && !isRecording()) {
        <div class="record-menu" (click)="$event.stopPropagation()">
          <div class="record-menu-title">Record format</div>
          @for (f of recordingFormats; track f) {
            <button class="record-item" (click)="startRecording(f)">{{ f }}</button>
          }
        </div>
      }
    </div>

    <!-- Fullscreen -->
    <button class="icon-btn fs-btn" (click)="toggleFullscreen()">
      @if (isFullscreen()) {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-14v3h3v2h-5V5h2z"/></svg>
      } @else {
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"><path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/></svg>
      }
    </button>
  </div>
</div>
